/**
 * ═══════════════════════════════════════════════════════════════════════════
 * COURSE COVER ART
 *
 * Most courses have no uploaded thumbnail, and a wall of identical grey
 * placeholders is unscannable — people recognise their training by its shape
 * and colour long before they read the title.
 *
 * So a course without a thumbnail gets a cover derived from its own id and
 * title: a colour and a symbol. Two properties matter and both are deliberate.
 *
 *   Stable — the same course looks the same on every screen, every visit, for
 *   every person. It is a hash of the id, not a random pick, so it can be
 *   recognised the way a book spine is.
 *
 *   Free — nobody has to author it. Uploading a real thumbnail still wins;
 *   this is what fills the gap until then, at no cost to whoever built the
 *   course.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * Deep, saturated grounds that hold white text at any size.
 *
 * Per-course rather than per-category: two compliance courses side by side are
 * easier to tell apart when they differ, and category is already named in
 * words directly under the cover.
 */
const COVERS = [
  '#9E2430', // crimson
  '#14503C', // forest
  '#8A5510', // amber
  '#453A78', // indigo
  '#A9541A', // burnt orange
  '#24508C', // blue
  '#125650', // teal
  '#6B2B52', // plum
] as const;

/**
 * Topic symbols, matched on the title.
 *
 * Two rules govern this table, and both are load-bearing:
 *
 *   Order — the first hit wins, so specific beats generic. "Food Safety and
 *   Kitchen Hygiene" must reach the plate before the chef, and a PII course
 *   must not be swallowed by the analytics rule on the word "data".
 *
 *   Anchoring — a stem carries `\b` only at the FRONT, because a trailing
 *   boundary makes the plural miss: `\banalytic\b` does not match
 *   "Analytics", since c→s is not a word boundary. Whole words and acronyms
 *   keep both anchors, or `\bpos\b` would fire on "position" and "possible".
 */
const SYMBOLS: [RegExp, string][] = [
  [/\b(pii|gdpr)\b|\bprivacy|\bdata protection|\bconfidential/i, '🔒'],
  [/\bsecurity|\binfosec|\bphishing|\bpassword|\bcyber/i, '🛡️'],
  [/\b(pos|kot)\b|\bbilling|\binvoic|\bcheckout|\border taking/i, '🧾'],
  [/\banalytic|\bdashboard|\breport|\bmetric|\binsight/i, '📊'],
  [/\bswiggy|\bzomato|\baggregator|\bdelivery|\brider|\bdispatch/i, '🛵'],
  [/\bsupport|\bcustomer|\bservice|\bescalation|\bticket/i, '🎧'],
  [/\bposh\b|\blegal|\bcomplian|\bpolic(y|ies)|\bregulat|\baudit/i, '⚖️'],
  [/\bhygiene|\bfood safety|\bfssai|\bsanitat/i, '🍽️'],
  [/\bkitchen|\bchef\b|\bcooking|\brecipe/i, '👨‍🍳'],
  [/\b(gst|tax)\b|\bpayment|\bfinanc|\baccounting|\brevenue|\bpricing/i, '💳'],
  [/\binventor|\bstock\b|\bprocurement|\bsupply|\bwastage/i, '📦'],
  [/\bmarketing|\bcampaign|\bpromotion|\bbrand\b|\bsocial media/i, '📣'],
  [/\brestaurant|\boutlet|\bstore\b|\bfranchise|\bbranch/i, '🏪'],
  [/\bhr\b|\bpeople\b|\bteam\b|\bhiring|\bonboard|\binduction/i, '👥'],
  [/\bsales\b|\bpitch|\blead generation|\bnegotiat|\bclosing/i, '🤝'],
  [/\bmenu|\bdish|\bcuisine|\bbeverage/i, '📖'],
  [/\binstall|\bsetup\b|\bconfigur|\bhardware|\bdevice|\bprinter/i, '🔧'],
  [/\btraining|\bcoach|\bfundamental|\bbasics\b|\b101\b/i, '🎓'],
];

/** Used when nothing in the title matches — still stable per course. */
const FALLBACK_SYMBOLS = ['📘', '🧭', '🗂️', '🧩', '💡', '🎯'] as const;

/**
 * FNV-1a. Small, dependency-free, and well spread over short strings — which
 * matters, because a weak hash would put every course on the same colour.
 */
function hash(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface CourseArt {
  color: string;
  symbol: string;
}

export function courseArt(id: string, title: string): CourseArt {
  const matched = SYMBOLS.find(([pattern]) => pattern.test(title));
  const seed = hash(id);

  return {
    color: COVERS[seed % COVERS.length]!,
    symbol: matched
      ? matched[1]
      : FALLBACK_SYMBOLS[seed % FALLBACK_SYMBOLS.length]!,
  };
}

/** "1h 15m", "55m" — the compact form the cover badge uses. */
export function compactDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
